import React from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { RootState } from '@/store';
import { Icons } from '@/utils/Icons';
import { useIsDemoUser } from '@/hooks/redux';
import { useGetContactSupportAccountQuery, useGetContactSupportQuery } from '@/apis/userApi';
import { buildWhatsappLink } from '@/utils/constants';
import { showToastMessage } from '@/helpers';

const navItems = [
  { id: 1, title: 'Casino', path: '/LIVECASINO', imgUrl: '/images/sidebar/casino-icon.png' },
  { id: 2, title: 'Sports', path: '/sports/cricket', imgUrl: '/images/sidebar/sports-icon.png' },
  { id: 3, title: 'Home', path: '/', isHome: true },
  { id: 4, title: 'Inplay', path: '/inplay', imgUrl: '/images/sidebar/inplay-icon.png' },
  { id: 5, title: 'Support', path: null, imgUrl: '/images/sidebar/whatsapp-icon.png' },
];

const navItemI18nKey: Record<string, string> = {
  Casino: 'mobileFooter.casino',
  Sports: 'mobileFooter.sports',
  Home: 'mobileFooter.home',
  Inplay: 'mobileFooter.inplay',
  Support: 'mobileFooter.support',
};

const MobileFooterBar = () => {
  const { t } = useTranslation();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isDemoUser = useIsDemoUser();
  const useAccountSupport = isAuthenticated && !isDemoUser;
  const { data: contactSupportAccount } = useGetContactSupportAccountQuery(undefined, {
    skip: !useAccountSupport,
  });
  const { data: contactSupport } = useGetContactSupportQuery(undefined, {
    skip: useAccountSupport,
  });
  const whatsappLink = buildWhatsappLink(
    useAccountSupport ? contactSupportAccount?.whatsapp : contactSupport?.whatsapp,
  );
  const showWhatsappSupport = !!whatsappLink;

  const handleSupportClick = () => {
    if (!whatsappLink) {
      showToastMessage('info', t('mobileFooter.supportUnavailable'));
      return;
    }
    window.open(`${whatsappLink}?text=${encodeURIComponent('Hi')}`, '_blank');
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[997] md:hidden bg-[#18162A] rounded-t-[25px]"
      style={{ height: '63.5px' }}
    >
      <div className="relative flex h-full items-center justify-around">
        {navItems.map((item) => {
          if (item.path === null) {
            return (
              <button
                key={item.id}
                type="button"
                onClick={handleSupportClick}
                disabled={!showWhatsappSupport}
                className="flex flex-col items-center justify-center gap-[3px] text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span
                  role="img"
                  aria-label={item.title}
                  className="w-[30px] h-[30px] inline-block bg-[#25D366]"
                  style={{
                    WebkitMaskImage: `url(${item.imgUrl})`,
                    maskImage: `url(${item.imgUrl})`,
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                  }}
                />
                <span className="font-medium" style={{ fontSize: '13px' }}>
                  {t(navItemI18nKey[item.title] ?? item.title, { defaultValue: item.title })}
                </span>
              </button>
            );
          }

          if (item.isHome) {
            return (
              <React.Fragment key={item.id}>
                {/* invisible spacer to hold the center slot */}
                <div className="w-14" />
                <NavLink
                  to={item.path!}
                  end
                  className="flex flex-col items-center gap-[3px] text-white absolute left-1/2 -translate-x-1/2 -top-4"
                >
                  <div className="w-14 h-14 rounded-full bg-[#FBAC40] flex items-center justify-center shadow-lg">
                    <Icons.Home size={36} />
                  </div>
                  <span className="font-medium" style={{ fontSize: '13px' }}>
                    {t('mobileFooter.home')}
                  </span>
                </NavLink>
              </React.Fragment>
            );
          }

          return (
            <NavLink
              key={item.id}
              to={item.path!}
              className="flex flex-col items-center justify-center gap-[3px] text-white"
            >
              <img
                src={item.imgUrl!}
                alt={item.title}
                className="w-[30px] h-[30px] object-contain"
              />
              <span className="font-medium" style={{ fontSize: '13px' }}>
                {t(navItemI18nKey[item.title] ?? item.title, { defaultValue: item.title })}
              </span>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
};

export default MobileFooterBar;
